import {
  CloseButton,
  VStack,
  Tabs,
  Spinner,
  Center,
  Alert,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useContactDetail, useUpdateContact, useAssignTag, useRemoveTag } from '../../hooks/useContactDetail'
import { useAuth } from '../../hooks/useAuth'
import {
  ContactProfileHeader,
  ContactInfoSection,
  DistrictsSection,
  ContactMethodArea,
  CanvassingHistorySection,
  NotesSection,
  SurveyResponsesSection,
  VoterRecordSection,
  ListMembershipsSection,
  TagsSection,
  EmploymentSection,
} from '.'
import { UnionOnly } from '../scoping/UnionOnly'

interface ContactDetailModalProps {
  contactId: string
  isOpen: boolean
  onClose: () => void
}

export function ContactDetailModal({ contactId, isOpen, onClose }: ContactDetailModalProps) {
  const { data: contact, isLoading, error } = useContactDetail(contactId)
  const updateMutation = useUpdateContact(contactId)
  const assignTagMutation = useAssignTag(contactId)
  const removeTagMutation = useRemoveTag(contactId)
  const { isOrganizer } = useAuth()

  const handleAssignTag = (tagId: string) => {
    assignTagMutation.mutate(tagId, {
      onSuccess: () => {
        toaster.create({ title: 'Tag assigned', type: 'success', duration: 2000 })
      },
      onError: () => {
        toaster.create({ title: 'Failed to assign tag', type: 'error', duration: 3000 })
      },
    })
  }

  const handleRemoveTag = (tagId: string) => {
    removeTagMutation.mutate(tagId, {
      onSuccess: () => {
        toaster.create({ title: 'Tag removed', type: 'success', duration: 2000 })
      },
      onError: () => {
        toaster.create({ title: 'Failed to remove tag', type: 'error', duration: 3000 })
      },
    })
  }

  const handleFieldSave = (fieldName: string, value: string) => {
    updateMutation.mutate(
      { [fieldName]: value },
      {
        onSuccess: () => {
          toaster.create({ title: 'Contact updated', type: 'success', duration: 2000 })
        },
        onError: () => {
          toaster.create({ title: 'Failed to update', type: 'error', duration: 3000 })
        },
      }
    )
  }

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="90vh">
            <Dialog.Header>Contact Details</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body pb={6}>
              {isLoading && (
                <Center py={20}>
                  <Spinner size="xl" color="accent" />
                </Center>
              )}

              {!!error && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  Contact not found or an error occurred.
                </Alert.Root>
              )}

              {!isLoading && contact && (
                <VStack gap={6} align="stretch">
                  <ContactProfileHeader contact={contact} />

                  <Tabs.Root variant='enclosed' colorPalette="brand" defaultValue="info">
                    <Tabs.List>
                      <Tabs.Trigger value="info">Contact Info</Tabs.Trigger>
                      <UnionOnly>
                        <Tabs.Trigger value="employment">Employment ({contact.employments.length})</Tabs.Trigger>
                      </UnionOnly>
                      <Tabs.Trigger value="tags">Tags ({contact.tags.length})</Tabs.Trigger>
                      <Tabs.Trigger value="history">History ({contact.canvassingSessions.length})</Tabs.Trigger>
                      <Tabs.Trigger value="notes">Notes ({contact.notes.length})</Tabs.Trigger>
                      <Tabs.Trigger value="surveys">Surveys ({contact.surveyResponseGroups.length})</Tabs.Trigger>
                      <Tabs.Trigger value="voterRecord">Voter Record</Tabs.Trigger>
                      <Tabs.Trigger value="lists">Lists ({contact.listMemberships.length})</Tabs.Trigger>
                    </Tabs.List>

                    <Tabs.ContentGroup>
                      <Tabs.Content value="info" px={0}>
                        <VStack gap={6} align="stretch">
                          <ContactInfoSection
                            contact={contact}
                            canEdit={isOrganizer}
                            onFieldSave={handleFieldSave}
                            isSaving={updateMutation.isPending}
                          />
                          <DistrictsSection contact={contact} canEdit={isOrganizer} />
                          <ContactMethodArea contact={contact} canEdit={isOrganizer} />
                        </VStack>
                      </Tabs.Content>

                      <Tabs.Content value="employment" px={0}>
                        <EmploymentSection
                          contactId={contactId}
                          employments={contact.employments}
                          canEdit={isOrganizer}
                        />
                      </Tabs.Content>

                      <Tabs.Content value="tags" px={0}>
                        <TagsSection
                          tags={contact.tags}
                          canEdit={isOrganizer}
                          onAssignTag={handleAssignTag}
                          onRemoveTag={handleRemoveTag}
                          isAssigning={assignTagMutation.isPending}
                          isRemoving={removeTagMutation.isPending}
                        />
                      </Tabs.Content>

                      <Tabs.Content value="history" px={0}>
                        <CanvassingHistorySection sessions={contact.canvassingSessions} />
                      </Tabs.Content>

                      <Tabs.Content value="notes" px={0}>
                        <NotesSection notes={contact.notes} />
                      </Tabs.Content>

                      <Tabs.Content value="surveys" px={0}>
                        <SurveyResponsesSection groups={contact.surveyResponseGroups} />
                      </Tabs.Content>

                      <Tabs.Content value="voterRecord" px={0}>
                        <VoterRecordSection voterRecord={contact.voterRecord} />
                      </Tabs.Content>

                      <Tabs.Content value="lists" px={0}>
                        <ListMembershipsSection memberships={contact.listMemberships} />
                      </Tabs.Content>
                    </Tabs.ContentGroup>
                  </Tabs.Root>
                </VStack>
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
